<template>
  <v-autocomplete
    ref="field"
    :attach="!topPosition"
    v-bind="$attrs"
    :items="serviceItems"
    :append-icon="loading ? 'mdi-loading mdi-spin' : 'mdi-chevron-down'"
    :data-cy="dataCy ? dataCy : false"
    autocomplete="disable"
    :menu-props="positionProps"
    :hide-details="showErrorsWithTooltip ? true : hideDetails"
    :clearable="isClearableSelect"
    v-on="$listeners"
    @update:search-input="onSearch"
    @update:error="setErrorsBucket"
    @blur="onBlur"
    @change="onChange">
    <template #message="{ message }">
      {{ parseError(message) }}
    </template>
    <template v-if="'chips' in $attrs" #selection="{ item, parent }">
      <v-chip
        :input-value="item"
        color="primary"
        outlined
        close
        @click:close="parent.selectItem(item)">
        {{ item[$attrs['item-text'] || 'title'] || item.name || item }}
      </v-chip>
    </template>

    <template #append>
      <v-tooltip v-if="showErrorsWithTooltip && errorsList" color="red" bottom>
        <template #activator="{ on }">
          <v-icon color="red" class="v-icon--circle error-icon-tooltip" v-on="on">$warning</v-icon>
        </template>
        {{ errorsList }}
      </v-tooltip>
      <slot v-else name="append"></slot>
    </template>
  </v-autocomplete>
</template>

<script>
import { debounce, cloneDeep } from 'lodash';

export default {
  name: 'CAutocomplete',
  inheritAttrs: false,
  props: {
    getItemsOnMount: { type: Boolean, default: true },
    topPosition: { type: Boolean, default: false },
    service: { type: String, default: '' },
    searchKey: { type: String, default: 'q' },
    items: { type: Array, default: () => ([]) },
    minSearchLength: { type: [String, Number], default: 3 },
    watch: { type: [String, Number, Object, Array, Boolean] },
    queryParams: { type: Object, default: null },
    urlParam: { type: [Number, String] },
    localSearch: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
    showErrorsWithTooltip: { type: Boolean, default: false },
    hideDetails: { type: [Boolean, String], default: false },
    useCache: { type: Boolean, default: true },
    clearSearchOnSelect: { type: Boolean, default: true },
    isClearableSelect: { type: Boolean, default: false },
    dataCy: { type: [String, Boolean], default: '' },
    filterFn: { type: Function, default: () => null },
    clearOnEmptyInput: { type: Boolean, default: false },
  },
  data() {
    return {
      search: '',
      loading: false,
      serviceItems: this.items,
      isWatch: false,
      reqParams: {},
      request: null,
      errorBucket: [],
    };
  },
  computed: {
    positionProps() {
      return this.topPosition ? { top: true, offsetY: true, offsetOverflow: false } : { nudgeBottom: 10, offsetY: true };
    },
    errorsList() {
      if (this.errorMessage) {
        return this.errorMessage;
      }

      return this.errorBucket.join(',');
    },
  },
  created() {
    if (this.service) {
      this.createRequestMethod();

      if (this.request) {
        if (this.watch !== 'undefined') {
          this.$watch('watch', () => {
            if (!this.urlParam && this.$refs.field && this.serviceItems.length) {
              this.$refs.field.reset();
              this.serviceItems = [];
            }
            this.setParams();

            if (this.getItemsOnMount) {
              this.getItems();
            }
          }, { immediate: true });
        } else {
          this.getItems();
        }
      }
    } else {
      this.$watch('items', () => { this.serviceItems = this.items; }, { immediate: true, deep: true });
    }
  },
  mounted() {
    this.setErrorsBucket();
  },
  methods: {
    createRequestMethod() {
      const [module, name] = this.service.split('/');

      this.request = this.$api[module][name];
    },
    setParams() {
      this.reqParams = this.$cleanPayload({ ...this.reqParams, ...this.queryParams });
    },
    getItems() {
      let request;

      if (typeof this.urlParam !== 'undefined') {
        if (this.urlParam) {
          request = this.request(this.urlParam, this.reqParams);
        }
      } else {
        request = this.request(this.reqParams);
      }

      if (request) {
        this.loading = true;

        request.then(({ data }) => {
          this.serviceItems = this.setItems(data.payload.results || data.payload);
          if (this.filterFn()) this.serviceItems = [...this.serviceItems.filter(this.filterFn())];

          this.$emit('requestResultUpdated', this.serviceItems);
        }).catch(err => this.$errorMessageHandler(err))
          .finally(() => this.loading = false);
      }
    },
    onChange() {
      if (this.clearSearchOnSelect) this.search = '';
    },
    onBlur() {
      if (!this.search) this.$emit('change', null);
      if (this.clearOnEmptyInput && !this.$attrs.value) this.serviceItems = [];
    },
    onSearch(val) {
      if (this.localSearch || !this.request) return;

      if (this.clearOnEmptyInput && val.length < +this.minSearchLength) {
        this.search = val;
        this.serviceItems = [];
        return;
      }

      if (val && val !== this.search && val.length >= +this.minSearchLength) {
        this.search = val;
        this.reqParams[this.searchKey] = this.search;
        this.getItemsBySearch();
      } else {
        this.search = val;
        this.reqParams[this.searchKey] = '';
        this.setParams();
      }
    },
    getItemsBySearch: debounce(function () {
      this.getItems();
    }, 400),
    setItems(newItems) {
      if (newItems && newItems.length) {
        const oldItems = this.useCache ? cloneDeep(this.serviceItems) : [];
        const itemValue = this.$attrs['item-value'];

        newItems.forEach(item => {
          let equalItem;
          if (itemValue) {
            equalItem = oldItems.find(i => i[itemValue] === item[itemValue]);
          } else {
            equalItem = oldItems.includes(item);
          }
          if (!equalItem) oldItems.push(item);
        });

        return oldItems;
      }
      return this.useCache ? this.serviceItems : newItems;
    },
    parseError(validationErr) {
      if (this.errorMessage) {
        return this.errorMessage;
      }

      try {
        const error = JSON.parse(validationErr);
        return this.$t(error.text, error.params);
      } catch (e) {
        return this.$t(validationErr);
      }
    },
    setErrorsBucket() {
      this.errorBucket = this.$refs.field.errorBucket.map(el => this.parseError(el));
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-select__selections {
    max-height: 200px;
    overflow: auto;
  }
}
.error-icon-tooltip {
  position: absolute;
  top: 50%;
  right: -8px;
  transform: translateY(-50%);
}
</style>
